import { useState, useEffect } from 'react';
import { Copy, Check, ExternalLink, ChevronDown } from 'lucide-react';
import { copyToClipboard, cn } from '@/lib/utils';

const PROVIDER_EXPANDED_KEY = 'sitemaphost-dns-providers-expanded';

interface CnameInstructionsProps {
  subdomain: string;
  targetCname: string;
}

export function CnameInstructions({ subdomain, targetCname }: CnameInstructionsProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [providersExpanded, setProvidersExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(PROVIDER_EXPANDED_KEY) === 'true';
    }
    return false;
  });

  // Extract just the subdomain part (e.g., "sitemap" from "sitemap.example.com")
  const subdomainPart = subdomain.split('.')[0];

  // Persist provider expanded state
  useEffect(() => {
    localStorage.setItem(PROVIDER_EXPANDED_KEY, String(providersExpanded));
  }, [providersExpanded]);

  const handleCopy = async (field: string, value: string) => {
    const success = await copyToClipboard(value);
    if (success) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const copyFullRecord = () => {
    const fullRecord = `Type: CNAME\nName: ${subdomainPart}\nTarget: ${targetCname}`;
    handleCopy('full', fullRecord);
  };

  return (
    <div className="space-y-4">
      {/* Main DNS Record Display */}
      <div className="relative bg-slate-900 rounded-xl overflow-hidden">
        <div className="absolute top-3 right-3 flex gap-2">
          <button
            onClick={copyFullRecord}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
              copiedField === 'full'
                ? 'bg-green-500 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            )}
          >
            {copiedField === 'full' ? (
              <>
                <Check className="w-3.5 h-3.5" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                Copy All
              </>
            )}
          </button>
        </div>

        <div className="p-6 pt-12 font-mono text-sm">
          <div className="grid grid-cols-[80px_1fr] gap-y-3">
            <span className="text-slate-500">Type:</span>
            <span className="text-cyan-400 font-semibold">CNAME</span>

            <span className="text-slate-500">Name:</span>
            <CopyableValue
              value={subdomainPart}
              copied={copiedField === 'name'}
              onCopy={() => handleCopy('name', subdomainPart)}
              className="text-yellow-400"
            />

            <span className="text-slate-500">Target:</span>
            <CopyableValue
              value={targetCname}
              copied={copiedField === 'target'}
              onCopy={() => handleCopy('target', targetCname)}
              className="text-green-400"
            />
          </div>
        </div>
      </div>

      {/* Provider-specific instructions - collapsible */}
      <div className="bg-slate-50 rounded-lg overflow-hidden">
        <button
          onClick={() => setProvidersExpanded(!providersExpanded)}
          className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-100 transition-colors"
        >
          <h4 className="font-medium text-slate-800">DNS Provider Instructions</h4>
          <ChevronDown
            className={cn(
              'w-5 h-5 text-slate-400 transition-transform',
              providersExpanded && 'rotate-180'
            )}
          />
        </button>
        {providersExpanded && (
          <div className="px-4 pb-4 grid gap-2 text-sm border-t border-slate-200 pt-3">
            <ProviderLink
              name="Cloudflare"
              url="https://developers.cloudflare.com/dns/manage-dns-records/how-to/create-dns-records/"
            />
            <ProviderLink
              name="GoDaddy"
              url="https://www.godaddy.com/help/add-a-cname-record-19236"
            />
            <ProviderLink
              name="Namecheap"
              url="https://www.namecheap.com/support/knowledgebase/article.aspx/9646/2237/how-to-create-a-cname-record-for-your-domain/"
            />
            <ProviderLink
              name="Google Domains"
              url="https://support.google.com/domains/answer/9211383"
            />
            <ProviderLink
              name="AWS Route 53"
              url="https://docs.aws.amazon.com/Route53/latest/DeveloperGuide/resource-record-sets-creating.html"
            />
          </div>
        )}
      </div>

      {/* Visual diagram */}
      <div className="border rounded-lg p-4">
        <h4 className="font-medium text-slate-800 mb-3">How it works</h4>
        <div className="flex items-center justify-center gap-4 text-sm">
          <div className="text-center">
            <div className="px-4 py-3 bg-blue-100 rounded-lg flex items-center justify-center font-mono text-blue-800 text-xs whitespace-nowrap">
              {subdomain}
            </div>
            <p className="text-xs text-slate-500 mt-1">Your subdomain</p>
          </div>
          <div className="flex items-center shrink-0">
            <div className="w-8 h-0.5 bg-slate-300" />
            <div className="w-0 h-0 border-t-4 border-b-4 border-l-6 border-transparent border-l-slate-300" />
          </div>
          <div className="text-center">
            <div className="px-4 py-3 bg-green-100 rounded-lg flex items-center justify-center font-mono text-green-800 text-xs whitespace-nowrap">
              {targetCname}
            </div>
            <p className="text-xs text-slate-500 mt-1">Points to SitemapHost</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CopyableValue({
  value,
  copied,
  onCopy,
  className,
}: {
  value: string;
  copied: boolean;
  onCopy: () => void;
  className?: string;
}) {
  return (
    <div className="flex items-center gap-2 group">
      <span className={className}>{value}</span>
      <button
        onClick={onCopy}
        className={cn(
          'p-1 rounded transition-all',
          copied
            ? 'text-green-400'
            : 'text-slate-500 opacity-0 group-hover:opacity-100 hover:text-slate-300'
        )}
        title="Copy to clipboard"
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function ProviderLink({ name, url }: { name: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 text-slate-600 hover:text-primary transition-colors"
    >
      <ExternalLink className="w-3.5 h-3.5" />
      {name}
    </a>
  );
}
